/**
 * Coverage the platform computes beside the DSCR the deal states, and the
 * maturity the terms do not reproduce.
 *
 * Amortizing coverage (DER-01) and interest-only coverage on the indicative rate
 * (DER-09) never replace the stated DSCR; they sit under it, each with its
 * state. The indicative one is a Suggestion — it rests on a rate the platform
 * priced from a dated SOFR print plus the stated spread, not on a rate any
 * document states — and says which print. A maturity the closing date and the
 * term do not reproduce (DER-11) is shown beside the stated one, for
 * information: the stated date stands and nothing is chosen (rule R4).
 *
 * Draws nothing when the deal supports none of it.
 */
import { CalendarClock } from 'lucide-react'

import { FieldStateBadge } from '@/components/fields/fieldState'
import type { LeverageModel } from './leverageModel'

export function CoverageExtras({ model }: { model: Pick<LeverageModel, 'coverageExtras' | 'maturityVariance'> }) {
  const { coverageExtras, maturityVariance } = model
  if (coverageExtras.length === 0 && !maturityVariance) return null
  return (
    <div data-testid="coverage-extras" className="space-y-0.5 border-t border-white/[0.04] pt-1 text-[9.5px]">
      {coverageExtras.map((extra) => (
        <div key={extra.id} data-extra={extra.id} data-basis={extra.basis} className="flex min-w-0 items-baseline gap-1.5">
          <span className="shrink-0 font-mono text-[8px] uppercase tracking-wide text-stone-500">{extra.label}</span>
          <span className="shrink-0 font-mono text-[11px] font-semibold text-stone-100">{extra.display}</span>
          <FieldStateBadge
            state={extra.basis === 'suggested' ? 'suggested' : extra.basis === 'derived' ? 'derived' : 'stated'}
            formula={extra.formula}
            title={
              extra.basis === 'suggested'
                ? `Suggested — ${extra.note ?? extra.formula}. Not a figure any document states.`
                : `Derived: ${extra.formula}`
            }
          />
          <span className="min-w-0 flex-1 truncate text-stone-500" title={extra.note ?? extra.formula}>
            {extra.note ?? extra.formula}
          </span>
        </div>
      ))}
      {maturityVariance ? (
        <p data-testid="maturity-variance" className="flex items-start gap-1 text-[9px] leading-snug text-amber-200/85">
          <CalendarClock size={10} className="mt-px shrink-0" />
          <span>
            Maturity is stated as {maturityVariance.stated}; the closing date and term give {maturityVariance.computed}
            {' '}— for information, the stated date stands.
          </span>
        </p>
      ) : null}
    </div>
  )
}
